/* Reference — the lessons, demoted (Milestone C).
 *
 * The 27 lessons stop being the spine and become the thing you reach for when a
 * build task blocks you. That is what reference material is: not a curriculum to
 * march through, but something you consult with a question already in hand.
 *
 * Deliberately plain. A filter and a list, opening the existing lesson player.
 * If this ever grows a reading streak or a completion ring, the demotion has
 * failed.
 */

import { useEffect, useMemo, useState } from 'react';
import { api, ApiFailure, type AtlasResponse } from '../api';
import { isUnitCompleted } from '../sync';

interface Entry {
  unitId: string;
  title: string;
  topicTitle: string;
  topicId: string;
  minutes: number;
}

export function ReferenceScreen({ onOpenUnit }: { onOpenUnit: (unitId: string) => void }) {
  const [atlas, setAtlas] = useState<AtlasResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<Set<string>>(new Set());
  const [q, setQ] = useState('');

  useEffect(() => {
    void (async () => {
      try {
        const a = await api.atlas();
        setAtlas(a);
        const completed = new Set<string>();
        for (const t of a.topics) {
          for (const u of t.units) {
            if (u.unit_type === 'lesson' && await isUnitCompleted(u.unit_id)) {
              completed.add(u.unit_id);
            }
          }
        }
        setDone(completed);
      } catch (err) {
        setError(err instanceof ApiFailure ? err.message : 'Something went wrong.');
      }
    })();
  }, []);

  const lessons = useMemo<Entry[]>(() => {
    const out: Entry[] = [];
    for (const t of atlas?.topics ?? []) {
      for (const u of t.units) {
        if (u.unit_type !== 'lesson') continue;
        out.push({
          unitId: u.unit_id, title: u.title, topicTitle: t.title,
          topicId: t.topic_id, minutes: u.est_minutes,
        });
      }
    }
    return out;
  }, [atlas]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return lessons;
    return lessons.filter(
      (l) => l.title.toLowerCase().includes(needle) || l.topicTitle.toLowerCase().includes(needle),
    );
  }, [lessons, q]);

  const byTopic = useMemo(() => {
    const m = new Map<string, Entry[]>();
    for (const l of filtered) m.set(l.topicTitle, [...(m.get(l.topicTitle) ?? []), l]);
    return [...m.entries()];
  }, [filtered]);

  if (error) {
    return (
      <main className="screen">
        <p className="eyebrow">Reference</p>
        <h1>Could not load</h1>
        <p>{error}</p>
      </main>
    );
  }

  return (
    <main className="screen">
      <p className="eyebrow">Reference</p>
      <h1>Background reading</h1>
      <p className="hint" style={{ marginTop: 0 }}>
        {lessons.length} lessons behind the tasks. Reach for one when something
        blocks you — there is no order to work through.
      </p>

      <input
        type="text"
        className="reference__filter"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Filter"
        aria-label="Filter lessons"
        autoCapitalize="off"
        autoCorrect="off"
      />

      {atlas && filtered.length === 0 ? (
        <p className="hint">Nothing matches “{q}”.</p>
      ) : null}

      {byTopic.map(([topicTitle, entries]) => (
        <section className="card" key={topicTitle}>
          <div className="card__title"><span>{topicTitle}</span></div>
          <ul className="unitlist">
            {entries.map((l) => (
              <li key={l.unitId}>
                <button
                  type="button"
                  className="unitlist__btn"
                  onClick={() => onOpenUnit(l.unitId)}
                >
                  <span className="unitlist__title">{l.title}</span>
                  <span className="unitlist__note">
                    {done.has(l.unitId) ? 'read' : `${l.minutes} min`}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </main>
  );
}
